import React, { useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { AuditListParams } from '../../../types/audit';
import { QUICK_RANGES, activeRangeKey, fromIsoToInput, toIsoFromInput, withQuickRange } from './auditTime';

const RESOURCE_TYPES = ['device', 'config_template', 'hardware_type', 'model_reference', 'firmware', 'user', 'client', 'programming'];

interface AuditEventFiltersProps {
  filters: AuditListParams;
  onChange: (filters: AuditListParams) => void;
  /** The four figures shown on the right of the toolbar. */
  counts: { total: number; success: number; failed: number; denied: number };
  loading: boolean;
  onExportCsv?: () => void;
}

/** The filter toolbar of the event list: period, type, status, the two switches, the advanced block, the figures. */
export const AuditEventFilters: React.FC<AuditEventFiltersProps> = ({ filters, onChange, counts, loading, onExportCsv }) => {
  const { formatNumber, t } = useI18n();
  // The advanced block opens by itself when one of its fields is set (deep links).
  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    Boolean(filters.resource_id || filters.action || filters.actor_username || filters.from_timestamp || filters.to_timestamp),
  );
  const advancedActiveCount = [filters.resource_id, filters.action, filters.actor_username, filters.from_timestamp, filters.to_timestamp].filter(Boolean).length;
  const activeRange = activeRangeKey(filters);
  const hasFilters = Object.keys(filters).some((k) => k !== 'authenticated_only');

  const setField = (key: keyof AuditListParams, value: string | boolean | undefined) => {
    const next: AuditListParams = { ...filters };
    if (value === '' || value === undefined || value === false) delete next[key];
    else (next as Record<string, unknown>)[key] = value;
    onChange(next);
  };

  return (
    <>
      <div className="tpl-toolbar aud-toolbar">
        <div className="aud-segment" role="group" aria-label={t('audit.filters.range')}>
          {QUICK_RANGES.map((r) => (
            <button key={r.key} type="button" className={`aud-segment-btn${activeRange === r.key ? ' active' : ''}`} onClick={() => onChange(withQuickRange(filters, r.hours))} aria-pressed={activeRange === r.key}>
              {t(`audit.filters.${r.key}`)}
            </button>
          ))}
        </div>

        <select id="audit-filter-resource-type" className="tpl-select" value={filters.resource_type || ''} onChange={(e) => setField('resource_type', e.target.value)} data-testid="audit-filter-resource-type" aria-label={t('audit.filters.resourceType')}>
          <option value="">{t('audit.filters.resourceTypeAny')}</option>
          {RESOURCE_TYPES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
          {filters.resource_type && !RESOURCE_TYPES.includes(filters.resource_type) && <option value={filters.resource_type}>{filters.resource_type}</option>}
        </select>

        <select id="audit-filter-status" className="tpl-select" value={filters.status || ''} onChange={(e) => setField('status', e.target.value)} data-testid="audit-filter-status" aria-label={t('audit.filters.status')}>
          <option value="">{t('audit.filters.statusAll')}</option>
          <option value="success">{t('audit.filters.statusSuccess')}</option>
          <option value="failed">{t('audit.filters.statusFailed')}</option>
          <option value="denied">{t('audit.filters.statusDenied')}</option>
        </select>

        <label className="tpl-check" htmlFor="audit-filter-mutations">
          <input id="audit-filter-mutations" type="checkbox" checked={!!filters.exclude_reads} onChange={(e) => setField('exclude_reads', e.target.checked)} data-testid="audit-filter-mutations" />
          {t('audit.filters.mutationsOnly')}
        </label>

        <label className="tpl-check" htmlFor="audit-filter-authenticated-only" title={t('audit.filters.authenticatedOnlyHint')}>
          <input id="audit-filter-authenticated-only" type="checkbox" checked={filters.authenticated_only !== false} onChange={(e) => onChange({ ...filters, authenticated_only: e.target.checked })} data-testid="audit-filter-authenticated-only" />
          {t('audit.filters.authenticatedOnly')}
        </label>

        <button type="button" className={`tpl-link aud-more${showAdvanced ? ' is-open' : ''}`} onClick={() => setShowAdvanced((v) => !v)} aria-expanded={showAdvanced} aria-controls="audit-advanced-filters" data-testid="audit-advanced-toggle">
          {t('audit.filters.more')}
          {advancedActiveCount > 0 && <span className="ud-tab-count" aria-label={t('audit.filters.activeCount', { count: advancedActiveCount })}>{advancedActiveCount}</span>}
          <span className="aud-chevron" aria-hidden="true">▾</span>
        </button>

        {hasFilters && (
          <button type="button" className="tpl-link" onClick={() => onChange({})} data-testid="audit-filter-reset">
            {t('audit.filters.reset')}
          </button>
        )}

        <div className="tpl-facts aud-facts">
          <span data-testid="audit-stat-total"><strong>{formatNumber(counts.total)}</strong> {t('audit.stats.total').toLowerCase()}</span>
          <span className="is-ok" data-testid="audit-stat-success"><strong>{formatNumber(counts.success)}</strong> {t('audit.stats.success').toLowerCase()}</span>
          <span className="is-bad" data-testid="audit-stat-failed"><strong>{formatNumber(counts.failed)}</strong> {t('audit.stats.failed').toLowerCase()}</span>
          <span className="is-warning" data-testid="audit-stat-denied"><strong>{formatNumber(counts.denied)}</strong> {t('audit.stats.denied').toLowerCase()}</span>
        </div>

        {onExportCsv && (
          <button type="button" className="btn btn-secondary btn-sm aud-export" onClick={onExportCsv} disabled={loading} data-testid="audit-export-csv">
            {t('audit.filters.exportCsv')}
          </button>
        )}
      </div>

      {showAdvanced && (
        <div id="audit-advanced-filters" className="aud-advanced">
          <div className="cu-field">
            <label htmlFor="audit-filter-resource-id">{t('audit.filters.resourceId')}</label>
            <input id="audit-filter-resource-id" type="text" className="cu-input mono" value={filters.resource_id || ''} placeholder={t('audit.filters.resourceIdPlaceholder')} onChange={(e) => setField('resource_id', e.target.value)} data-testid="audit-filter-resource-id" />
          </div>
          <div className="cu-field">
            <label htmlFor="audit-filter-action">{t('audit.filters.action')}</label>
            <input id="audit-filter-action" type="text" className="cu-input mono" value={filters.action || ''} placeholder={t('audit.filters.actionPlaceholder')} onChange={(e) => setField('action', e.target.value)} data-testid="audit-filter-action" />
          </div>
          <div className="cu-field">
            <label htmlFor="audit-filter-actor">{t('audit.filters.actor')}</label>
            <input id="audit-filter-actor" type="text" className="cu-input" value={filters.actor_username || ''} placeholder={t('audit.filters.actorPlaceholder')} onChange={(e) => setField('actor_username', e.target.value)} data-testid="audit-filter-actor" />
          </div>
          <div className="cu-field">
            <label htmlFor="audit-filter-from">{t('audit.filters.fromTimestamp')}</label>
            <input id="audit-filter-from" type="datetime-local" className="cu-input" value={fromIsoToInput(filters.from_timestamp)} onChange={(e) => setField('from_timestamp', toIsoFromInput(e.target.value) || '')} data-testid="audit-filter-from" />
          </div>
          <div className="cu-field">
            <label htmlFor="audit-filter-to">{t('audit.filters.toTimestamp')}</label>
            <input id="audit-filter-to" type="datetime-local" className="cu-input" value={fromIsoToInput(filters.to_timestamp)} onChange={(e) => setField('to_timestamp', toIsoFromInput(e.target.value) || '')} data-testid="audit-filter-to" />
          </div>
        </div>
      )}
    </>
  );
};
